/**
 * Shared types, defaults and option metadata for the studio's theming.
 *
 * Two independent axes:
 *  - APPEARANCE: the user's choice of `light` | `dark` | `auto` (auto follows the
 *    OS `prefers-color-scheme`). Resolves to a {@link ResolvedMode} at runtime.
 *  - ACCENT: the brand colour — `default` (the original orange) | `red` | `green`
 *    | `blue`. Applies in both light and dark.
 *
 * Persisted under one reserved preferences namespace (cross-device) AND in
 * localStorage (so the boot script can apply it before first paint, no flash).
 */

/** What the user picks for appearance. */
export type ThemeMode = 'auto' | 'light' | 'dark';
/** What `auto` resolves to once the OS preference is read. */
export type ResolvedMode = 'light' | 'dark';
/** The brand accent. The app has ONE brand colour (orange #ff791b); kept as a
 *  single-member type so the (now fixed) plumbing still type-checks. */
export type Accent = 'default';

export const THEME_MODES: ThemeMode[] = ['auto', 'light', 'dark'];
export const ACCENTS: Accent[] = ['default'];

// Default appearance when the user has made no choice yet (no saved preference
// and no localStorage). The studio ships dark-first, so a brand-new account
// starts dark regardless of the OS setting until they pick `auto`/`light`.
export const DEFAULT_THEME_MODE: ThemeMode = 'dark';
export const DEFAULT_ACCENT: Accent = 'default';

/** localStorage keys — read synchronously by the boot script in index.html. */
export const THEME_MODE_STORAGE_KEY = 'ows-theme-mode';
export const ACCENT_STORAGE_KEY = 'ows-accent';

/** `<html>` attributes the generated stylesheet keys off. */
export const THEME_ATTR = 'data-ows-theme'; // resolved: "light" | "dark"
export const ACCENT_ATTR = 'data-ows-accent'; // "default" | "red" | "green" | "blue"

/** Reserved top-level preferences key (see store/slices/userPreferences). */
export const APPEARANCE_PREF_KEY = 'appearance';

/** The persisted appearance preference shape. */
export type AppearancePrefs = {
  mode: ThemeMode;
  accent: Accent;
};

export const defaultAppearancePrefs = (): AppearancePrefs => ({
  mode: DEFAULT_THEME_MODE,
  accent: DEFAULT_ACCENT,
});

export const isThemeMode = (value: unknown): value is ThemeMode =>
  value === 'auto' || value === 'light' || value === 'dark';
export const isAccent = (value: unknown): value is Accent => value === 'default';

/** Settings-UI copy (English natural-language keys, translation-ready). */
export const THEME_MODE_OPTIONS: { value: ThemeMode; label: string; hint: string }[] = [
  { value: 'auto', label: 'Auto', hint: 'Match your system appearance' },
  { value: 'light', label: 'Light', hint: 'Always use the light theme' },
  { value: 'dark', label: 'Dark', hint: 'Always use the dark theme' },
];

/**
 * Fixed display colours for the mini-studio illustration drawn inside each
 * appearance tile (Auto / Light / Dark). These are deliberately HARD-CODED per
 * resolved mode (not theme tokens) so every tile always shows what that mode
 * looks like, regardless of the studio's currently-active theme. Mirrors the
 * real surfaces: app background, raised card, side rail, hairline borders, two
 * text tones, and the brand accent.
 */
export type ModeSwatch = {
  bg: string;
  card: string;
  rail: string;
  line: string;
  text: string;
  muted: string;
  accent: string;
};

export const MODE_SWATCH: Record<ResolvedMode, ModeSwatch> = {
  dark: {
    bg: '#0b0b0e',
    card: '#1c1c20',
    rail: '#141418',
    line: 'rgba(255, 255, 255, 0.12)',
    text: 'rgba(255, 255, 255, 0.82)',
    muted: 'rgba(255, 255, 255, 0.30)',
    accent: '#ff791b',
  },
  light: {
    bg: '#e9ebef',
    card: '#ffffff',
    rail: '#f4f5f8',
    line: 'rgba(17, 19, 26, 0.10)',
    text: 'rgba(17, 19, 26, 0.78)',
    muted: 'rgba(17, 19, 26, 0.26)',
    accent: '#ff791b',
  },
};

/** Hairline drawn on the diagonal seam of the Auto tile (neutral, reads on both halves). */
export const MODE_SPLIT_LINE = 'rgba(125, 130, 140, 0.55)';
